import React, { useState, useMemo, useEffect, useRef } from 'react';
import './selectLanguage.css';
import { translateUiText } from '../i18n';

const SelectLanguage = ({ title, onClose, onSelectLanguage, languages, userLanguageBrowser, locale = 'en' }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const currentLangRef = useRef(null);

  // Filter languages based on search query
  const filteredLanguages = useMemo(() => {
    if (!searchQuery.trim()) {
      return languages;
    }

    const query = searchQuery.toLowerCase();
    return languages.filter(language => {
      const name = language.name.toLowerCase();
      const translatedName = language.languageTranslation[userLanguageBrowser]?.toLowerCase() || '';
      return name.includes(query) || translatedName.includes(query);
    });
  }, [searchQuery, languages, userLanguageBrowser]);

  // Scroll to current language on mount
  useEffect(() => {
    if (currentLangRef.current) {
      currentLangRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button
            className="button-close-x"
            onClick={onClose}
            aria-label={translateUiText(locale, 'close')}
          >
            ×
          </button>
        </div>

        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder={translateUiText(locale, 'searchLanguagesPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button
              className="clear-search"
              onClick={() => setSearchQuery('')}
              aria-label={translateUiText(locale, 'clearSearch')}
            >
              ×
            </button>
          )}
        </div>

        <div className="language-count">
          {translateUiText(
            locale,
            filteredLanguages.length === 1 ? 'languageCountSingular' : 'languageCountPlural',
            { count: filteredLanguages.length }
          )}
        </div>

        <div className="language-list">
          {filteredLanguages.length > 0 ? (
            filteredLanguages.map((language, index) => {
              const isCurrentLanguage = language.language === userLanguageBrowser;
              return (
                <div
                  key={index}
                  ref={isCurrentLanguage ? currentLangRef : null}
                  className={`item_container ${isCurrentLanguage ? 'current-language' : ''}`}
                  onClick={() => onSelectLanguage(language)}
                >
                  <div className="item_language">
                    <img className='img_style' src={language.imgUrl} alt={language.name} />
                    <div className="language-text">
                      {language.languageTranslation[userLanguageBrowser] ? (
                        <>
                          <span className="item_text_primary">{language.languageTranslation[userLanguageBrowser]}</span>
                          {language.name !== language.languageTranslation[userLanguageBrowser] && (
                            <span className="item_text_secondary">{language.name}</span>
                          )}
                        </>
                      ) : (
                        <span className="item_text_primary">{language.name}</span>
                      )}
                    </div>
                    <span className="language-code">{language.language.toUpperCase()}</span>
                    {isCurrentLanguage && <span className="current-indicator">✓</span>}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-results">
              <p>{translateUiText(locale, 'noLanguagesFound', { query: searchQuery })}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectLanguage;
